import { execFile } from "node:child_process";
import { promisify } from "node:util";
import { getCurrentBranch, hasUncommittedChanges } from "./git-utils.js";

// Async git (execFile, no shell) — synchronous child processes freeze the
// agent's event loop and starve the socket heartbeat; see git-utils.ts.
const execFileAsync = promisify(execFile);

/**
 * Handler for `session:pullBranch` events. Fired against a parent task's
 * connected session when a child PR merges into the parent's branch (feature
 * branch packs). Performs a fast-forward `git pull` of the parent's own
 * branch so the workspace and preview reflect merged child work.
 *
 * No-op (with a warning) if the workspace is dirty or on a different branch —
 * we never want to surprise the agent or the user by overwriting in-flight work.
 */
export async function handlePullBranch(workDir: string, branch: string): Promise<void> {
  if (!branch) return;

  const current = await getCurrentBranch(workDir);
  if (current !== branch) {
    process.stderr.write(
      `[conveyor-agent] pull_branch ignored — current branch ${current ?? "(detached)"} != ${branch}\n`,
    );
    return;
  }

  if (await hasUncommittedChanges(workDir)) {
    process.stderr.write(
      `[conveyor-agent] pull_branch ignored — uncommitted changes on ${branch}\n`,
    );
    return;
  }

  try {
    await execFileAsync("git", ["fetch", "origin", branch], { cwd: workDir, timeout: 60_000 });
  } catch {
    process.stderr.write(`[conveyor-agent] pull_branch: fetch failed for ${branch}\n`);
    return;
  }

  try {
    await execFileAsync("git", ["pull", "--ff-only", "origin", branch], {
      cwd: workDir,
      timeout: 60_000,
    });
    process.stderr.write(`[conveyor-agent] pull_branch: pulled origin/${branch}\n`);
  } catch {
    process.stderr.write(
      `[conveyor-agent] pull_branch: ff-only pull failed for ${branch} (likely diverged)\n`,
    );
  }
}
